/**
 * @packageDocumentation
 * @module f1/fn
 */

import debounce from './debounce';

type VoidFn = (...args: unknown[]) => void;

export interface Delay {
  /**
   * Same as debounce, except this is made to be written in an
   * event handler because we do not generate methods.
   *
   * @note delay does not curry, it is made to be called inside of a method repeatedly
   * @public
   * @function f1.delay
   * @param key the key is the unique debounce method, this allows us to embed the method in an event handler
   * @param delay the time to wait before executing the function, in milliseconds
   * @param fn the function to execute
   * @example
   *
   * ```ts
   * const eventHandler = (e) => {
   *   f1.delay('key', 1000, (_e) => processEvent(_e), e)
   * }
   *
   * // we can call this and the inner method is protected
   * eventHandler(e)
   * ```
   */
  <F extends VoidFn, V>(key: string, delay: number, fn: F | null, ...value: unknown[]): F | void;
}

const dM = new Map();

/**
 * @see Delay
 */
const d = <F extends VoidFn, V>(key: string, delay: number, fn: F | null, ...value: unknown[]): F | void => {
  if (fn === null) {
    dM.delete(key);
    return;
  }

  const dFn = (dM.get(key) as F) || debounce(delay, fn);

  dM.set(key, dFn);
  dFn(...value);
};

d.map = dM;
export default d;
